<template>
  <AppLayout>
    <div class="container">
      <h6 class="fw-bold">Bulk Upload Products</h6>
      <p class="text-muted">Upload a CSV file to create multiple products at once. Download the template to see the required format.</p>

      <div class="panel">
        <!-- File selection -->
        <div class="row align-items-end g-3 mb-3">
          <div class="col-md-8">
            <label class="form-label fw-semibold">Select CSV file</label>
            <input
              type="file"
              accept=".csv"
              ref="fileInput"
              @change="onFileChange"
              :disabled="uploading"
              class="form-control"
            />
            <small class="text-muted">
              CSV must include headers: <code>ProductName</code>, <code>ProductCategory</code>, <code>Unit</code>, <code>SubUnit</code>.
              All other columns are optional.
            </small>
          </div>
          <div class="col-md-4 d-flex gap-2">
            <button
              class="btn btn-primary flex-fill"
              :disabled="uploading || !selectedFile"
              @click="submit"
            >
              <span v-if="uploading">
                <span class="spinner-border spinner-border-sm me-1"></span>Uploading…
              </span>
              <span v-else>Upload</span>
            </button>
            <a
              :href="`${baseUrl}/files/exports/ProductTemplate.csv`"
              class="btn btn-style-2 flex-fill"
              target="_blank"
              download
            >
              Download Template
            </a>
          </div>
        </div>

        <!-- File name preview -->
        <div v-if="selectedFile" class="mb-3">
          <span class="badge bg-light text-dark border">
            📄 {{ selectedFile.name }} ({{ fileSizeKb }} KB)
          </span>
          <button type="button" class="btn btn-link btn-sm text-danger p-0 ms-2" @click="clearFile">Remove</button>
        </div>

        <!-- Progress bar -->
        <div v-if="uploading || uploadPercent > 0" class="mb-3">
          <div class="d-flex justify-content-between mb-1">
            <small class="text-muted">Uploading…</small>
            <small class="text-muted">{{ uploadPercent }}%</small>
          </div>
          <div class="progress" style="height: 18px;">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              :style="{ width: uploadPercent + '%' }"
              :aria-valuenow="uploadPercent"
              aria-valuemin="0"
              aria-valuemax="100"
            >{{ uploadPercent }}%</div>
          </div>
        </div>

        <!-- Success result -->
        <div v-if="result && result.status === true" class="alert alert-success d-flex align-items-start gap-2">
          <span>✅</span>
          <div>
            <strong>Upload successful!</strong>
            <div>{{ result.message || (result.created + ' products created.') }}</div>
            <div v-if="result.created != null" class="mt-1">
              <span class="badge bg-success me-1">{{ result.created }} created</span>
              <span v-if="result.skipped" class="badge bg-warning text-dark">{{ result.skipped }} skipped</span>
            </div>
          </div>
        </div>

        <!-- Failure result -->
        <div v-else-if="result && result.status === false" class="alert alert-danger">
          <strong>Upload failed:</strong> {{ result.message || 'An error occurred. See details below.' }}
        </div>

        <!-- Row-level errors -->
        <div v-if="rowErrors.length" class="mt-3">
          <h6 class="text-danger">Row errors ({{ rowErrors.length }})</h6>
          <div class="errors-grid">
            <div v-for="(e, i) in rowErrors" :key="i" class="error-card">
              <div class="error-card-header">
                Row {{ e.row || e.line || (i + 2) }}
                <span v-if="e.name" class="fw-normal ms-2 text-muted" style="font-size:11px;">{{ e.name }}</span>
              </div>
              <div class="error-card-body">
                <template v-if="Array.isArray(e.errors) && e.errors.length">
                  <ul class="error-issues">
                    <li v-for="(msg, idx) in e.errors" :key="idx" class="error-issue">
                      <span class="error-badge">Error</span>
                      <span class="error-text">{{ msg }}</span>
                    </li>
                  </ul>
                </template>
                <template v-else-if="e.error">
                  <div class="error-single">
                    <span class="error-badge">Error</span>
                    <span class="error-text">{{ e.error }}</span>
                  </div>
                </template>
                <template v-else>
                  <span class="error-text">{{ JSON.stringify(e) }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue'
import axios from 'axios'

const BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '')

export default {
  name: 'BulkUploadProducts',
  components: { AppLayout },

  data() {
    return {
      baseUrl: BASE_URL,
      selectedFile: null,
      uploading: false,
      uploadPercent: 0,
      result: null,
      rowErrors: [],
    }
  },

  computed: {
    fileSizeKb() {
      if (!this.selectedFile) return 0
      return (this.selectedFile.size / 1024).toFixed(1)
    },
  },

  methods: {
    onFileChange(e) {
      const file = e.target.files?.[0] || null
      this.selectedFile = file
      this.result = null
      this.rowErrors = []
      this.uploadPercent = 0
    },

    clearFile() {
      this.selectedFile = null
      this.result = null
      this.rowErrors = []
      this.uploadPercent = 0
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    async submit() {
      if (!this.selectedFile) return
      this.uploading = true
      this.result = null
      this.rowErrors = []
      this.uploadPercent = 0

      const fd = new FormData()
      fd.append('file', this.selectedFile, this.selectedFile.name)

      try {
        const token = JSON.parse(localStorage.getItem('access_token'))
        const res = await axios.post(`${BASE_URL}/products/upload`, fd, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          onUploadProgress: (e) => {
            if (e.total) {
              this.uploadPercent = Math.round((e.loaded * 100) / e.total)
            }
          },
        })
        this.result = res.data
        // Normalise row errors from various possible shapes
        const data = res.data
        if (data?.errors && Array.isArray(data.errors)) {
          this.rowErrors = data.errors
        } else if (data?.data && Array.isArray(data.data)) {
          this.rowErrors = data.data.filter(r => r.errors || r.error)
        }
      } catch (err) {
        this.result = err.response?.data || { status: false, message: err.message }
        const errData = err.response?.data
        if (errData?.errors) this.rowErrors = errData.errors
      } finally {
        this.uploading = false
      }
    },
  },
}
</script>

<style scoped>
.panel {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  margin-top: 11px;
}

.errors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 11px;
  margin-top: 8px;
}

.error-card {
  border: 1px solid #f5c6cb;
  background: #fff6f6;
  padding: 11px;
  border-radius: 6px;
}

.error-card-header {
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 11px;
  color: #a71d2a;
}

.error-issues {
  list-style: none;
  padding: 0;
  margin: 0;
}

.error-issue {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}

.error-single {
  display: flex;
  gap: 8px;
  align-items: center;
}

.error-badge {
  background: #a71d2a;
  color: #fff;
  padding: 2px 8px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.error-text {
  color: #333;
  font-size: 11px;
  word-break: break-word;
}

@media (max-width: 576px) {
  .errors-grid { grid-template-columns: 1fr; }
}
</style>
